import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Typography,
  TableRow,
  TableCell,
  TablePagination,
  Divider,
  Tooltip,
  alpha,
  Chip,
} from "@mui/material";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import axios from "axios";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useSelector } from "react-redux";
import { COUNTRYWITHCODE } from "../constants";
import DataTable from "../../Common/DataTable";

export default function ProfileInfo() {
  const theme = localStorage.getItem("theme");
  const [avatar, setAvatar] = useState("https://via.placeholder.com/100");
  const [loginHistory, setLoginHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { userInfo, userProfileImage, btncolor } = useSelector((state) => state);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("uuid", localStorage.getItem("uuid"));
    formData.append("profilePic", file);

    try {
      const response = await axios.post(
        WEBSERVICE + "/user/uploadProfilePicture",
        formData,
        {
          headers: {
            Authorization: `BEARER ${localStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.error?.error_data !== 0) {
        alert("Error: " + response.data.error?.error_msg);
      } else {
        const imageUrl = URL.createObjectURL(file);
        localStorage.setItem("profile_pic", imageUrl);
        setAvatar(imageUrl);
        showToast("success", "Profile photo uploaded successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      showToast("error", "Upload failed. Please try again.");
    }
  };

  const countryData = () => {
    if (userInfo?.userResult?.country) {
      return userInfo?.userResult?.country;
    } else {
      const filterCountry = COUNTRYWITHCODE.filter(
        (d) => d?.dial_code === userInfo?.userResult?.countryCode
      );
      return filterCountry[0]?.name;
    }
  };

  const getLoginHistory = async (pageNo) => {
    setLoginHistory(null);
    const logHistoryObj = {
      uuid: localStorage.getItem("uuid"),
      noOfItemsPerPage: rowsPerPage,
      pageNo: pageNo + 1,
    };
    try {
      const response = await fetch(WEBSERVICE + "/user/GetLoginHistory", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "BEARER " + localStorage.getItem("access_token"),
        },
        body: JSON.stringify(logHistoryObj),
      });
      const data = await response.json();
      setLoginHistory(data.loginDetails);
      setTotalCount(data.totalRows);
    } catch (error) {
      console.error("Error fetching login history:", error);
    }
  };

  useEffect(() => {
    getLoginHistory(page);
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (userInfo) {
      const img =
        "https://accounts.paybito.com/api/user/" +
        userInfo?.userResult?.uuid +
        "/file/" +
        userInfo?.userResult?.profilePic +
        "?access_token=" +
        localStorage.getItem("access_token");
      setAvatar(img);
    }
  }, [userInfo]);

  const infoRows = [
    {
      icon: <EmailOutlinedIcon sx={infoIconStyle} />,
      label: "Email",
      value: userInfo?.userResult?.email,
    },
    {
      icon: <PhoneOutlinedIcon sx={infoIconStyle} />,
      label: "Phone Number",
      value: userInfo?.userResult?.phone,
    },
    {
      icon: <PublicOutlinedIcon sx={infoIconStyle} />,
      label: "Country",
      value: countryData(),
    },
    {
      icon: <LocationOnOutlinedIcon sx={infoIconStyle} />,
      label: "Address",
      value: userInfo?.userResult?.address,
    },
    {
      icon: <CalendarTodayOutlinedIcon sx={infoIconStyle} />,
      label: "Joining Date",
      value: userInfo?.userResult?.created,
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AccountCircleOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>My Profile</Typography>
            <Typography sx={heroSubtitle}>
              Manage your personal information and review your account login
              activity
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Profile Card ── */}
      <Box sx={mainCard}>
        {/* Identity row */}
        <Box sx={identityRow}>
          {/* Avatar with upload */}
          <Box sx={avatarWrap}>
            <Avatar
              src={userProfileImage || avatar}
              sx={{
                width: 90,
                height: 90,
                border: `3px solid var(--body-color-less)`,
                boxShadow: `0 4px 16px rgba(0,0,0,0.22)`,
              }}
            />
            <input
              type="file"
              accept="image/*"
              id="avatar-upload"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor="avatar-upload">
              <Tooltip title="Change photo" arrow>
                <IconButton
                  component="span"
                  sx={{
                    position: "absolute",
                    bottom: 3,
                    right: 3,
                    width: 26,
                    height: 26,
                    backgroundColor: btncolor || "#1976d2",
                    color: "#fff",
                    border: "2px solid var(--body-color-less)",
                    "&:hover": { backgroundColor: btncolor || "#1976d2", opacity: 0.85 },
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </label>
          </Box>

          {/* Name + chip */}
          <Box sx={identityMeta}>
            <Typography sx={profileName}>
              {userInfo?.userResult?.firstName}{" "}
              {userInfo?.userResult?.lastName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip
                label="Active"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  backgroundColor: "rgba(22,163,74,0.12)",
                  color: "#16a34a",
                  border: "1px solid rgba(22,163,74,0.28)",
                }}
              />
              <Typography sx={profileIdText}>
                ID: {userInfo?.userResult?.userId}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.12)", mx: { xs: 2, md: 3 } }} />

        {/* Info grid */}
        <Box sx={infoGrid}>
          {infoRows.map((row, i) => (
            <Box key={i} sx={infoItem}>
              <Box sx={infoLabelRow}>
                {row.icon}
                <Typography sx={infoLabel}>{row.label}</Typography>
              </Box>
              <Typography sx={infoValue}>{row.value || "—"}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Login History ── */}
      <Box sx={sectionCard}>
        <Box sx={sectionHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryOutlinedIcon
              sx={{ fontSize: 18, color: btncolor, opacity: 0.85 }}
            />
            <Typography sx={sectionTitle}>Login History</Typography>
          </Box>
          <Typography sx={sectionCaption}>
            Recent account access activity
          </Typography>
        </Box>

        <Box sx={tableOverride}>
        <DataTable
          columns={[
            "IP Address",
            "Latitude",
            "Longitude",
            "Location",
            "Browser",
            "Device Type",
            "Status",
            "Date-Time",
          ]}
        >
          {loginHistory ? (
            loginHistory.map((row, index) => (
              <TableRow key={index} sx={modernTableBodyRow}>
                <TableCell sx={modernTableBodyCell}>{row.ipAddress}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.latitude}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.longitude}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.location}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.browser}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.deviceType}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.status}</TableCell>
                <TableCell sx={modernTableBodyCell}>{row.timestamp}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} sx={emptyCell}>
                <InboxOutlinedIcon sx={emptyIcon} />
                <Typography sx={emptyText}>Loading...</Typography>
              </TableCell>
            </TableRow>
          )}
        </DataTable>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={paginationStyle}
        />
        </Box>
      </Box>
    </Box>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

// Profile card
const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  mb: 3,
};

// Identity row
const identityRow = {
  display: "flex",
  alignItems: "center",
  gap: 2.5,
  px: { xs: 2.5, md: 3.5 },
  py: { xs: 2.5, md: 3 },
  flexWrap: "wrap",
};

const avatarWrap = {
  position: "relative",
  display: "inline-block",
  flexShrink: 0,
};

const identityMeta = {
  pb: 0.5,
};

const profileName = {
  fontSize: { xs: "1.1rem", md: "1.25rem" },
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
  letterSpacing: "-0.01em",
};

const profileIdText = {
  fontSize: "0.72rem",
  color: "var(--text-color)",
  opacity: 0.45,
  fontWeight: 500,
};

const infoGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
  gap: { xs: 2, md: 2.5 },
  px: { xs: 2.5, md: 3.5 },
  pb: { xs: 2.5, md: 3 },
  pt: 2,
};

const infoItem = {
  display: "flex",
  flexDirection: "column",
  gap: 0.4,
};

const infoLabelRow = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const infoIconStyle = {
  fontSize: 13,
  color: "var(--text-color)",
  opacity: 0.45,
};

const infoLabel = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.45,
};

const infoValue = {
  fontSize: "0.88rem",
  fontWeight: 500,
  color: "var(--text-color)",
  lineHeight: 1.4,
  wordBreak: "break-word",
};

// Login History section
const sectionCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeader = {
  px: { xs: 2.5, md: 3 },
  pt: { xs: 2.5, md: 3 },
  pb: 2,
  borderBottom: "1px solid rgba(125,125,125,0.13)",
  backgroundColor: "var(--body-color-less2)",
};

const sectionTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const sectionCaption = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.5,
  mt: 0.3,
  pl: "26px",
};

// Table
const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const paginationStyle = {
  borderTop: "1px solid rgba(125,125,125,0.15)",
  backgroundColor: "var(--body-color-less2)",
  color: "var(--text-color)",
  "& .MuiTablePagination-toolbar": {
    minHeight: 52,
    color: "var(--text-color)",
  },
  "& .MuiTablePagination-displayedRows": {
    fontSize: "0.82rem",
    color: "var(--text-color)",
  },
  "& .MuiTablePagination-selectLabel": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)" },
  "& .MuiIconButton-root.Mui-disabled .MuiSvgIcon-root": { opacity: 0.35 },
  "& .MuiSelect-select": { color: "var(--text-color)" },
};

const tableOverride = {
  "& .MuiTableCell-head": {
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontWeight: 700,
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    opacity: 0.75,
    borderBottom: "1px solid rgba(125,125,125,0.15)",
    py: 1.75,
    whiteSpace: "nowrap",
  },
  "& .MuiTableContainer-root": {
    backgroundColor: "var(--body-color-less)",
    boxShadow: "none",
  },
  "& .MuiTableBody-root": {
    backgroundColor: "var(--body-color-less)",
  },
};
